// ULTRA ARCADE - CENTRALIZED ROUTE CONFIGURATION
// All routes defined in one place for easy management

export const routes = {
  // Public Routes
  home: { path: '/', title: 'Home', icon: 'Home' },
  addicting: { path: '/addicting', title: 'Most Addicting', icon: 'Flame' },
  hot: { path: '/hot', title: 'Hot Games', icon: 'Flame', badge: 'HOT' },
  new: { path: '/new', title: 'New Games', icon: 'Sparkles' },
  trending: { path: '/trending', title: 'Trending', icon: 'TrendingUp' },
  random: { path: '/random', title: 'Random Game', icon: 'Shuffle' },
  
  // Game Routes
  game: { path: '/game/[slug]', title: 'Play Game' },
  category: { path: '/category/[slug]', title: 'Category' },
  tag: { path: '/tag/[tag]', title: 'Tag' },
  search: { path: '/search', title: 'Search Results', icon: 'Search' },
  
  // Content Routes
  blog: { path: '/blog', title: 'Blog', icon: 'Newspaper' },
  blogPost: { path: '/blog/[slug]', title: 'Blog Post' },
  page: { path: '/page/[slug]', title: 'Page' },
  
  // User Routes
  login: { path: '/login', title: 'Login', auth: false },
  register: { path: '/register', title: 'Register', auth: false },
  profile: { path: '/profile', title: 'My Profile', auth: true, icon: 'User' },
  favorites: { path: '/favorites', title: 'Favorites', auth: true, icon: 'Heart' },
  history: { path: '/history', title: 'Recently Played', auth: true, icon: 'History' },
  leaderboard: { path: '/leaderboard', title: 'Leaderboard', icon: 'Trophy' },
};

// Admin Routes (protected)
export const adminRoutes = {
  dashboard: { path: '/admin', title: 'Dashboard', icon: 'LayoutDashboard' },
  games: { path: '/admin/games', title: 'Games', icon: 'Gamepad2' },
  addicting: { path: '/admin/addicting', title: 'Addicting Games', icon: 'Flame' },
  homeCollections: { path: '/admin/home-collections', title: 'Home Collections', icon: 'LayoutDashboard' },
  import: { path: '/admin/import', title: 'Import Center', icon: 'Download' },
  metrics: { path: '/admin/metrics', title: 'Performance', icon: 'BarChart3' },
  categories: { path: '/admin/categories', title: 'Categories', icon: 'FolderTree' },
  comments: { path: '/admin/comments', title: 'Comments', icon: 'MessageSquare' },
  users: { path: '/admin/users', title: 'Users', icon: 'Users' },
  blog: { path: '/admin/blog', title: 'Blog Posts', icon: 'FileText' },
  pages: { path: '/admin/pages', title: 'Pages', icon: 'File' },
  ads: { path: '/admin/ads', title: 'Ad Zones', icon: 'Megaphone' },
  seo: { path: '/admin/seo', title: 'SEO Settings', icon: 'Search' },
  seoManager: { path: '/admin/seo-system', title: 'SEO Manager', icon: 'Search' },
  plugins: { path: '/admin/plugins', title: 'Plugins', icon: 'Puzzle' },
  themes: { path: '/admin/themes', title: 'Themes', icon: 'Palette' },
  settings: { path: '/admin/settings', title: 'Settings', icon: 'Settings' },
};

// Category routes with icons
export const categoryRoutes = [
  { slug: 'action', name: 'Action', icon: 'Zap', color: '#ef4444' },
  { slug: 'racing', name: 'Racing', icon: 'Car', color: '#f97316' },
  { slug: 'puzzle', name: 'Puzzle', icon: 'Brain', color: '#eab308' },
  { slug: 'sports', name: 'Sports', icon: 'Trophy', color: '#22c55e' },
  { slug: 'shooter', name: 'Shooter', icon: 'Crosshair', color: '#06b6d4' },
  { slug: 'strategy', name: 'Strategy', icon: 'Wand2', color: '#8b5cf6' },
  { slug: 'adventure', name: 'Adventure', icon: 'Sparkles', color: '#ec4899' },
  { slug: 'arcade', name: 'Arcade', icon: 'Gamepad2', color: '#f43f5e' },
  { slug: 'casual', name: 'Casual', icon: 'Heart', color: '#14b8a6' },
  { slug: 'kids', name: 'Kids', icon: 'Smile', color: '#a855f7' },
  { slug: 'multiplayer', name: 'Multiplayer', icon: 'Users', color: '#3b82f6' },
  { slug: 'io', name: '.io Games', icon: 'Globe', color: '#10b981' },
];

// API endpoints configuration
export const apiRoutes = {
  // Games
  games: '/api/games',
  game: (slug) => `/api/games/${slug}`,
  gamesByCategory: (slug) => `/api/categories/${slug}/games`,
  gamesHot: '/api/games/hot',
  gamesNew: '/api/games/new',
  gamesTrending: '/api/games/trending',
  gamesRandom: '/api/games/random',
  gamesRelated: (slug) => `/api/games/${slug}/related`,
  
  // Categories
  categories: '/api/categories',
  category: (slug) => `/api/categories/${slug}`,
  
  // Search
  search: '/api/search',
  searchSuggestions: '/api/search/suggestions',
  
  // Social
  vote: '/api/vote',
  comments: '/api/comments',
  commentsByGame: (gameId) => `/api/games/${gameId}/comments`,
  
  // User
  auth: {
    login: '/api/auth/login',
    register: '/api/auth/register',
    logout: '/api/auth/logout',
    me: '/api/auth/me',
    refresh: '/api/auth/refresh',
  },
  user: {
    profile: '/api/users/me',
    favorites: '/api/users/me',
    history: '/api/users/history',
    achievements: '/api/users/achievements',
  },
  
  // Blog
  posts: '/api/posts',
  post: (slug) => `/api/posts/${slug}`,
  
  // Pages
  pages: '/api/pages',
  page: (slug) => `/api/pages/${slug}`,
  
  // Admin
  admin: {
    dashboard: '/api/admin/dashboard',
    stats: '/api/admin/stats',
    metrics: '/api/admin/metrics',
    games: '/api/admin/games',
    import: '/api/admin/import',
    importStatus: '/api/admin/import/status',
    categories: '/api/admin/categories',
    comments: '/api/admin/comments',
    users: '/api/admin/users',
    posts: '/api/admin/posts',
    pages: '/api/admin/pages',
    ads: '/api/admin/ads',
    seo: '/api/admin/seo',
    plugins: '/api/admin/plugins',
    settings: '/api/admin/settings',
    activityLog: '/api/admin/activity',
  },
};

// Helper function to build route with params
export function getRoute(routeKey, params = {}) {
  const route = routes[routeKey] || adminRoutes[routeKey];
  if (!route) return routeKey;
  
  let path = route.path;
  Object.entries(params).forEach(([key, value]) => {
    path = path.replace(`[${key}]`, value);
  });
  return path;
}

// Helper to get route metadata
export function getRouteInfo(path) {
  const allRoutes = { ...routes, ...adminRoutes };
  return Object.values(allRoutes).find(r => r.path === path);
}

// Navigation items for sidebar
export const sidebarNav = {
  main: [
    routes.home,
    routes.addicting,
    routes.hot,
    routes.new,
    routes.trending,
    routes.random,
  ],
  categories: categoryRoutes,
  user: [
    routes.profile,
    routes.favorites,
    routes.history,
    routes.leaderboard,
  ],
};

// Admin navigation
export const adminNav = [
  adminRoutes.dashboard,
  adminRoutes.games,
  adminRoutes.addicting,
  adminRoutes.homeCollections,
  adminRoutes.import,
  adminRoutes.metrics,
  adminRoutes.categories,
  adminRoutes.comments,
  adminRoutes.users,
  adminRoutes.blog,
  adminRoutes.pages,
  adminRoutes.ads,
  adminRoutes.seo,
  adminRoutes.seoManager,
  adminRoutes.plugins,
  adminRoutes.themes,
  adminRoutes.settings,
];

export default routes;
